import React, { useEffect, useState } from "react"
import { Platform } from "react-native"
import Animated, {
  interpolate,
  useAnimatedStyle,
} from "react-native-reanimated"

import styled from "styled-components/native"

import { useEventEmitterValue } from "../../hooks/use-event-emitter-value"
import { useKeepAwake } from "../../hooks/use-keep-awake"
import { useLockBodyScroll } from "../../hooks/use-lock-body-scroll"
import { OpenState, useOpenAnimation } from "../../hooks/use-open-animation"
import { ebookReader, EbookReaderEvent } from "../../lib/ebook-reader"
import { syncManager } from "../../watermelon/sync"
import { Portal } from "../portal"
import { SystemBars } from "../system-bars"
import { useTokens } from "../tokens-provider"
import { ViewerWithHud } from "./viewer-with-hud"

const OPEN_ANIMATION_TRANSLATE_Y = 50

const Container = styled(Animated.View)`
  position: ${Platform.OS === "web" ? "fixed" : "absolute"};
  width: 100%;
  height: 100%;
  background-color: ${(props) => props.theme.colors.background.primary};
`

export function EbookReaderFullscreen(): React.JSX.Element {
  const ebook = useEventEmitterValue(
    ebookReader,
    EbookReaderEvent.EbookChanged,
    ebookReader.getEbook,
  )
  const [open, setOpen] = useState(ebook !== null)
  const [openAnimation, openState] = useOpenAnimation({ open })

  // Keep the device awake for slow readers
  useKeepAwake(openState === "open")

  // Don't allow the page behind the reader to scroll
  useLockBodyScroll(openState === "open")

  // Open the reader when an ebook is loaded
  useEffect(() => {
    if (ebook) {
      setOpen(true)
    }
  }, [ebook])

  // Close the reader when requested
  useEffect(
    () =>
      ebookReader.on(EbookReaderEvent.CloseRequested, () => {
        setOpen(false)
        // Request an immediate sync to persist progress to the server
        syncManager.requestSync({ syncType: "immediate" })
      }),
    [],
  )

  // Clear the ebook when closed
  useEffect(() => {
    if (openState === "closed") {
      ebookReader.setEbook(null)
    }
  }, [openState])

  const containerStyle = useAnimatedStyle(() => ({
    opacity: openAnimation.value,
    transform: [
      {
        translateY: interpolate(
          openAnimation.value,
          [0, 1],
          [OPEN_ANIMATION_TRANSLATE_Y, 0],
        ),
      },
    ],
  }))

  // NOTE: We do not unmount the portal when the reader is closed because of a
  // bug with @gorhom/portal. For an unknown reason unmounting one portal while
  // mounting another (e.g. closing the chapter selector on the ebook page while
  // opening the ebook reader) results in an extra unmounted/remounted cycle on
  // the new portal, failed animations, etc.
  if (openState === "closed") {
    return <Portal />
  }

  return (
    <Portal>
      <EbookReaderSystemBars openState={openState} />
      <Container style={containerStyle}>
        {ebook ? (
          <ViewerWithHud ebook={ebook} animating={openState !== "open"} />
        ) : null}
      </Container>
    </Portal>
  )
}

function EbookReaderSystemBars({ openState }: { openState: OpenState }) {
  const theme = useEventEmitterValue(
    ebookReader,
    EbookReaderEvent.ThemeChanged,
    ebookReader.getTheme,
  )
  const { displayMode } = useTokens()
  const colorScheme = theme.colorScheme[displayMode]

  // If the ebook reader isn't in an open state then bail
  if (openState !== "opening" && openState !== "open") {
    return null
  }

  // On android we keep the nav and status bar visible to avoid a host of layout
  // jitter and keyboard-avoiding-view bugs. To make this look nice we update
  // the colors of the bars to match the selected ebook theme.
  if (Platform.OS === "android") {
    return (
      <>
        <SystemBars
          style={{
            statusBar: colorScheme.displayMode,
            navigationBar: colorScheme.displayMode,
          }}
          onPressBack={ebookReader.requestClose}
        />
      </>
    )
  }

  // Everywhere other than Android we simply hide the status bar.
  return <SystemBars hidden />
}
